import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useI18n, type Language } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { languageOptions } from "./languages";

export function LanguagePreferenceDialog({ enabled }: { enabled: boolean }) {
  const { language, hasLanguagePreference, confirmLanguagePreference, t } =
    useI18n();
  const [draftLanguage, setDraftLanguage] = useState<Language>(language);
  const [isSaving, setIsSaving] = useState(false);
  const open = enabled && !hasLanguagePreference;

  useEffect(() => {
    if (open) {
      setDraftLanguage(language);
    }
  }, [language, open]);

  async function handleConfirm() {
    setIsSaving(true);
    try {
      await confirmLanguagePreference(draftLanguage);
    } catch {
      // Keep the local confirmation; the next authenticated load retries the account sync.
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open={open}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-sm gap-5 rounded-lg p-4 sm:p-6 [&>button:last-child]:hidden">
        <DialogHeader className="text-left">
          <DialogTitle>{t("language.promptTitle")}</DialogTitle>
          <DialogDescription>
            {t("language.promptDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          {languageOptions.map((item) => {
            const selected = item.code === draftLanguage;
            return (
              <button
                key={item.code}
                type="button"
                aria-label={t(item.nameKey)}
                aria-pressed={selected}
                onClick={() => setDraftLanguage(item.code)}
                className={cn(
                  "flex min-h-12 w-full items-center justify-center gap-3 rounded-md border px-3 text-left transition-colors",
                  selected
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-input bg-background hover:bg-accent",
                )}
              >
                <span className="text-2xl leading-none" aria-hidden="true">
                  {item.flag}
                </span>
                <span className="sr-only">{t(item.nameKey)}</span>
                {selected ? <Check className="h-4 w-4 shrink-0" /> : null}
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            type="button"
            className="w-full"
            onClick={handleConfirm}
            disabled={isSaving}
          >
            {t("language.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
